import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
} from '@angular/forms';
import { TelePhoneService } from './telephone.service';
import { COUNTRIES_ID } from '@shared/enum/country.enum';
import intlTelInput, { Plugin } from 'intl-tel-input';

@Component({
  selector: 'app-telephone-input',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './telephone-input.component.html',
  styleUrls: ['./telephone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TelephoneInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TelephoneInputComponent),
      multi: true,
    },
  ],
})
export class TelephoneInputComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor, OnChanges
{
  countryCode: string;
  dialCode: string = '1';
  nationCode: string;
  phoneNumber: string = '';
  iti: Plugin;
  @ViewChild('telInput') telInput!: ElementRef;
  @ViewChild('telContainer') telContainer!: ElementRef;
  @Input() errorValidation?: boolean = false;
  @Input() readonly?: boolean = false;
  @Input() disableAutoCountrySelect: boolean = false;
  @Input() instanceId: string;
  @Input() isSameAsContactInfo: boolean = false;
  @Output() finishValidationEmit = new EventEmitter<any>();
  constructor(private telePhoneService: TelePhoneService) {}
  private elemOpenDropdownListener: (() => void) | undefined;
  private elemCloseDropdownListener: (() => void) | undefined;

  ngAfterViewInit(): void {
    const selectedCountryCode =
      this.telePhoneService.getCountryCode(this.instanceId) || this.getUserCountryCode();

    this.configPhoneInput(this.telInput, selectedCountryCode);

    if (this.pendingPhoneValue) {
      this.phoneNumber = this.pendingPhoneValue.number || '';
      this.dialCode = this.pendingPhoneValue.dial_code?.replace('+', '') || '';
      const iso2 = this.getIso2FromDialCode(this.dialCode, (this.iti as any)?.countries || []);

      if (iso2) {
        this.iti.setCountry(iso2);
      }

      this.handleOnchange();
      this.pendingPhoneValue = null;
    }

    if (this.iti && !this.dialCode) {
      const selectedCountryData = this.iti.getSelectedCountryData();
      if (selectedCountryData) {
        this.dialCode = selectedCountryData.dialCode;
        this.handleOnchange();
      }
    }

    if (this.disableAutoCountrySelect) {
      (this.iti as any).telInput.removeEventListener('keyup', (this.iti as any)._handleKeyupEvent);
    }

    this.reStyleItiArrow('.iti__arrow', this.telInput, false);
    if (this.iti && !selectedCountryCode) {
      this.countryCode = this.getIso2FromDialCode(this.dialCode, (this.iti as any).countries);
      this.telePhoneService.setCountryCode(this.instanceId, this.countryCode);
      this.iti?.setCountry(this.countryCode);
    }
    this.telInput.nativeElement?.addEventListener('countrychange', () => {
      this.onCountryCodeChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isSameAsContactInfo'] &&
      changes['isSameAsContactInfo'].currentValue === true &&
      this.instanceId === 'adult_infomation_0'
    ) {
      const selectedCountryCode = this.telePhoneService.getCountryCode('contact_information');
      if (selectedCountryCode) {
        this.iti?.setCountry(selectedCountryCode);
      }
      setTimeout(() => {
        this.handleOnchange();
        this.finishValidationEmit.emit();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.telInput.nativeElement?.removeEventListener('open:countrydropdown', () => {});
    this.telInput.nativeElement?.removeEventListener('close:countrydropdown', () => {});
  }

  private pendingPhoneValue: any = null;

  writeValue(value: any): void {
    if (value) {
      this.dialCode = value?.dial_code ? value?.dial_code.slice(1) : '';
      this.phoneNumber = value?.number ? value?.number : '';
      this.pendingPhoneValue = value;

      if (this.iti) {
        const iso2 = this.getIso2FromDialCode(this.dialCode, (this.iti as any)?.countries || []);
        if (iso2) {
          this.iti.setCountry(iso2);
        }
      }
    }
  }

  onChange: Function = () => {};

  onTouch: Function = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  getUserCountryCode(): string {
    const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}');
    return userProfile.country_code ? userProfile.country_code.toLowerCase() : 'us';
  }

  configPhoneInput(el: any, code?: string) {
    if (typeof code == 'undefined' || code === '') {
      code = this.getUserCountryCode();
    }

    this.iti = intlTelInput(el.nativeElement, {
      initialCountry: code,
      separateDialCode: true,
      formatOnDisplay: true,
      nationalMode: true,
    });
  }

  reStyleItiArrow(className: string, elem: ElementRef, isGetCode: boolean) {
    const arrowBtn = this.telContainer.nativeElement.querySelector(className);

    if (arrowBtn) {
      const chevronDownIcon = document.createElement('i');
      chevronDownIcon.className = 'sctr-icon-chevron-down';

      arrowBtn.classList.add('text-gray-500', 'text-xl');

      arrowBtn.appendChild(chevronDownIcon);

      elem.nativeElement?.addEventListener('open:countrydropdown', () => {
        chevronDownIcon.classList.remove('sctr-icon-chevron-down');
        chevronDownIcon.classList.add('sctr-icon-chevron-up');
        if (isGetCode) this.nationCode = this.iti.getSelectedCountryData().iso2;
      });

      elem.nativeElement?.addEventListener('close:countrydropdown', () => {
        chevronDownIcon.classList.add('sctr-icon-chevron-down');
        chevronDownIcon.classList.remove('sctr-icon-chevron-up');
        if (isGetCode) this.nationCode = this.iti.getSelectedCountryData().iso2;
      });
    }
  }

  onCountryCodeChange() {
    const selectedCountryData = this.iti.getSelectedCountryData();
    this.dialCode = selectedCountryData.dialCode;
    this.countryCode = selectedCountryData.iso2;
    this.telePhoneService.setCountryCode(this.instanceId, this.countryCode);
    this.handleOnchange();
  }

  handleOnchange(event?: any) {
    if (this.phoneNumber) {
      this.onChange({ dial_code: '+' + this.dialCode, number: this.phoneNumber });
    } else {
      this.onChange({ dial_code: '', number: '' });
    }
  }

  getIso2FromDialCode(dialCode: string, countries: any[]) {
    const found = countries.filter((country) => country.dialCode === dialCode);
    const selected = found.find((country) => country.priority === 0);
    return selected ? selected.iso2 : COUNTRIES_ID.US;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.iti && this.iti.getNumber() && !this.iti.isValidNumber()
      ? { invalidPhone: true }
      : null;
  }
}
